import i18n from '@/i18n'
import { filterRulesMapped } from '@/tools/filterRules'
import { personService } from '@/services/person.service'
import { mapActions, mapGetters} from 'vuex'
import { DialogType, SnackbarStyle } from '@/constants/constants'
import PersonDialogComponent from '@/components/person/dialog/PersonDialogComponent.vue'
import { dialogTypes } from '@/constants/dialogTypes'
import { DialogPerson, dialogUtil } from '@/tools/dialog'
import { clone } from 'ramda'
import Vue from 'vue'
import {enterpriseService} from "@/services/enterprise.service";
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue";

/**
 * Componente del ABM de Persona (tabla y dialog de creación/modificación)
 */
export default {
    name: 'PersonABMComponent',
    components: {
        PersonDialogComponent,
        AutocompleteComponent
    },
    data: () => ({
        // headers
        tableHeaders: [],
        // reglas de filtrado
        tableFilterRules: {},
        // datos de la tabla
        tableData: [],
        // key de los datos
        tableDataKey: '_id',
        // acciones (botones) de cada fila
        rowActions: [
            // modificar
            {
                id: 1,
                name: i18n.t('modify'),
                color: 'blue',
                icon: 'fa-pen'
            },
            // eliminar
            {
                id: 2,
                name: i18n.t('delete'),
                color: 'red',
                icon: 'fa-trash-alt'
            }
        ],
        // botones que se agregan a la tabla
        tableComponentButtons: [],
        // tamaño de página para el paginador
        pageSize: undefined,
        // número de página para el paginador
        pageNumber: undefined,
        // total de objetos (personas) de la base de datos
        pageTotal: 0,
        // dialog creación/modificación (componente y modelo)
        dialog: {
            ...dialogTypes.ENTERPRISE,
        },
        // para mostrar/ocultar el dialog de creación/modificación
        showEditDialog: false,
        // título e ícono del dialog
        editDialogTitle: i18n.t('person.editDialog.titleNew'),
        editDialogIcon: 'mdi-account-plus',
        // para activar/desactivar el loading de la tabla
        loadingTable: true,
        // arreglo de empresas del selector
        enterprises: [],
        // empresa seleccionada
        enterpriseSelected: null,
        // Opción 'TODOS' para el selector de empresa
        all: {
            _id: 'ALL',
            name: i18n.t('all')
        },
    }),
    computed: {
        /**
         * Breadcrumbs de los dialog
         */
        ...mapGetters('breadcrumbs', [
            'getBread'
        ]),
        /**
         * Valor computado para hacer las requests al api
         */
        enterpriseSelectedArray() {
            if (this.enterpriseSelected) {
                if (this.enterpriseSelected === this.all._id) {
                    return this.enterprises.filter(enterprise => enterprise._id !== this.all._id)
                        .map(enterprise => enterprise._id)
                } else {
                    return [this.enterpriseSelected]
                }
            } else {
                return []
            }
        }
    },
    created() {
        /*
            Vuex watch del breadcrumb
        */
        this.unwatch = this.$store.watch(
            (state, getters) => getters['breadcrumbs/getBread'],
                (newBreads) => {
                    const lastElemet = newBreads[newBreads.length - 1]
                    if (!lastElemet) {
                        this.cancel()
                        return
                    }
                    this.dialog = dialogUtil.getDialog(lastElemet.key, DialogPerson)
                },
            { deep: true })
    },
    beforeDestroy() {
        // elimino la instancia del watcher del breadcrumb
        this.unwatch()
        this.cleanBread()
    },
    mounted() {
        this.initialize()
        this.resetFields()
        this.openPerson()
    },
    methods: {
        /**
         * actions del breadcrumbs
         */
        ...mapActions('breadcrumbs', [
            'pushBread',
            'cleanBread'
        ]),
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * Para inicializar el componente
         */
        async initialize () {
            // Botones del TableComponent
            this.tableComponentButtons.push(
                {
                    id: 'edit-dialog',
                    tooltip: i18n.t('add'),
                    icon: 'add',
                    action: this.openCreateDialog.bind(this),
                    selectedData: false
                },
                {
                    id: 'masive-delete',
                    tooltip: i18n.t('multipleDelete'),
                    icon: 'delete_outline',
                    action: this.multipleDelete.bind(this),
                    selectedData: true
                })

            // empresas
            await this.initEnterpriseSelector()

            // total de personas (para el paginador)
            this.pageTotal = await personService.getTotal(this.enterpriseSelectedArray)

            // Si hay query params en la URL, los setea para incluirlos en la llamada al backend
            if (this.$route.query.pageSize && this.$route.query.pageNumber) {
                this.pageSize = Number(this.$route.query.pageSize)
                this.pageNumber = Number(this.$route.query.pageNumber)

                // Valida que los qery params sean correctos y completos
                if ((this.pageSize && this.pageSize <= 0) || (this.pageNumber && this.pageNumber <= 0) || (this.pageNumber && !this.pageSize) || (!this.pageNumber && this.pageSize)) {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('error.invalidQueryParams'),
                        timeout: 6000,
                        style: SnackbarStyle.ERROR
                    })
                    this.pageSize = this.pageTotal
                    this.pageNumber = 1
                }
            } else {
                this.pageSize = this.pageTotal
                this.pageNumber = 1
            }

            // cargo la tabla con las cosas filtradas de acuerdo a los valores del selector
            this.getData()
        },
        /**
         * Refrescar la data
         */
        refreshTable() {
            this.getData(true)
        },
        /**
         * Para obtener los datos de la tabla
         * @param {*} update
         */
        async getData(update = false) {
            this.loadingTable = true
            if (update) {
                this.pageTotal = await personService.getTotal(this.enterpriseSelectedArray)
                this.pageSize = 50
                this.pageNumber = 1
            }
            const data = await personService.findAll(this.pageSize, this.pageNumber, !update, this.enterpriseSelectedArray)
            const lastTableHeaders = clone(this.tableHeaders)
            this.tableData.cleanAndUpdate(data.data)
            if (update) {
                const that = this
                Vue.nextTick(function () {
                    that.tableHeaders.cleanAndUpdate(lastTableHeaders)
                })
            } else {
                this.tableHeaders.cleanAndUpdate(data.headers)
                this.setTableFilterRules()
            }
            this.loadingTable = false
        },
        /**
         * Crea las reglas de filtrado
         */
        setTableFilterRules() {
            this.tableFilterRules = {}
            this.tableHeaders.forEach(header => {
                this.tableFilterRules[header.value] = filterRulesMapped[header.filterType]
            })
        },
        /**
         * Acción lanzada cuando se hace click en el botón eliminar de una fila de la tabla
         * @param {*} id
         */
        async delete(id) {
            await personService.deleteOne(id)
            this.getData(true)
        },
        /**
         * Eliminación múltiple
         * @param {*} data
         */
        async multipleDelete(data) {
            this.openDialog({
                title: this.$t('person.multipleDeleteTitle'),
                text: this.$t('person.multipleDeleteText'),
                type: DialogType.QUESTION,
                overlayOpacity: 0.5,
                actionButtons: true,
                yesAction: async () => {
                    const ids = data.map(e => e._id)
                    await personService.deleteMany(ids)
                    this.getData(true)
                },
                noAction: () => {
                }
            })
        },
        /**
         * Carga los datos para la edición
         * @param {*} data
         */
        async loadFields(id) {
            const data = await personService.findOneById(id)
            if (data) {
                this.openEditDialog()
                const newModel = {
                    _id: data._id,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    cuit: data.cuit,
                    dni: data.dni,
                    dateOfBirth: data.dateOfBirth,
                    civilStatus: data.civilStatus,
                    enterpriseIds: data.enterpriseIds,

                    address: data.address,
                    cityId: data.cityId,
                    district: data.district,
                    provinceId: data.provinceId,
                    countryId: data.countryId,
                    zipCode: data.zipCode,

                    phone1: data.phone1,
                    phone2: data.phone2,
                    whatsapp: data.whatsapp,
                    sms: data.sms,
                    email1: data.email1,
                    email2: data.email2,

                    comment: data.comment,

                    driverCode: data.driverCode,

                    update: true
                }
                const model = this.dialog.model
                Object.assign(model, newModel)
            } else {
                this.showSnackbar({
                    visible: true,
                    text: this.$t('person.error.couldNotLoadEditDialog'),
                    timeout: 6000,
                    style: SnackbarStyle.ERROR
                })
            }
        },
        rowButtonClicked(button, data) {
            switch (button.id) {
                case 1:
                    // TO DO: agregar el id como query param
                    this.editDialogTitle = i18n.t('person.editDialog.titleUpdate')
                    this.editDialogIcon = 'mdi-account-edit'
                    this.loadFields(data._id)
                    break
                case 2:
                    this.openDialog({
                        title: this.$t('person.deleteTitle'),
                        text: this.$t('person.deleteText', {name: `${data.firstName} ${data.lastName}`}),
                        type: DialogType.QUESTION,
                        overlayOpacity: 0.5,
                        actionButtons: true,
                        yesAction: () => {
                            this.delete(data._id)
                        },
                        noAction: () => {
                        }
                    })
                    break
                default:
                    console.error('Botón no programado en el componente')
            }
        },
        /**
         * Si se hace click fuera del dialog, se ejecuta la misma función que el botçon cancelar
         */
        clickOutside() {
            this.cancel()
        },
        /**
         * Botón cancelar
         */
        cancel() {
            this.showEditDialog = false
            this.resetFields()
        },
        /**
         * Resetea los campos del formulario de creación/actualización
         */
        resetFields() {
            this.dialog.model = {
                _id: null,
                firstName: null,
                lastName: null,
                cuit: null,
                dni: null,
                dateOfBirth: null,
                civilStatus: null,
                enterprise: null,

                address: null,
                cityId: null,
                district: null,
                provinceId: null,
                countryId: null,
                zipCode: null,

                phone1: null,
                phone2: null,
                whatsapp: null,
                sms: null,
                email1: null,
                email2: null,

                comment: null,

                driverCode: null,

                update: false,

                enterpriseIds: [],
            }
        },
        /**
         * Para activar el diálogo de creación
         */
        openCreateDialog() {
            this.openPerson()
            this.resetFields()
            this.editDialogTitle = i18n.t('person.editDialog.titleNew')
            this.editDialogIcon = 'mdi-account-plus'
            this.showEditDialog = true
        },
        /**
         * Para activar el diálogo de modificación
         */
        openEditDialog() {
            this.openPerson()
            this.dialog.model.update = true
            this.showEditDialog = true
        },
        /**
         * Configura el breadcrumb del diálogo
         */
        openPerson() {
            const bread = {
                key: DialogPerson,
                disabled: true,
                text: this.$t('person.name')
            }
            this.pushBread(bread)
        },
        /**
         * Para actualizar los valores del paginador
         * @param {*} pageSize
         * @param {*} pageNumber
         */
        externalPaginator(pageSize, pageNumber) {
            this.pageNumber = Number(pageNumber)
            this.pageSize = Number(pageSize)
        },
        /**
         * Inicializa el selector de empresas
         */
        async initEnterpriseSelector() {
            this.enterprises = await enterpriseService.getAllForSelector()
            if (this.enterprises.length > 1) {
                this.enterprises = ([this.all]).concat(this.enterprises)
                this.enterpriseSelected = this.all._id
            } else if (this.enterprises.length === 1) {
                this.enterpriseSelected = this.enterprises[0]._id
            }
        },
        /**
         * Cada vez que se modifica la selección de empresa, se cargan
         */
        enterpriseSelectedFunction (newValue) {
            this.enterpriseSelected = newValue._id ? newValue._id : newValue;
            this.getData(true)
        },
    },
}
